<template lang="pug">
div(
    class="bg-white text-black p-3 cursor-default overflow-hidden"
    :class="{'shadow-lg border border-gray-light': showDropShadow}"
)
    div(class="flex items-center pb-3 text-lg font-bold uppercase") Products In Cart
        span(class="m-1 text-xs normal-case") ({{ numberize(itemCount, 'Item') }})

    div
        div(class="items overflow-y-auto overflow-x-hidden")
            div(
                v-for='item in cart.items'
                data-cypress='cartItem'
                class="pt-3 mr-0 mb-4 border-t border-gray-light"
            )
                div(class="flex")
                    div(class="w-1/4 mr-4")
                        img(
                            :src="$getImageUrl(item.image.key, item.image.filename, 200, 150)"
                            width="200"
                            height="150"
                        )
                    div(class="w-3/4")
                        div(class="mb-1")
                            nuxt-link(
                                :to="{ name: 'sku', params: { productLine: item.productLineSlug, skuSlug: item.skuSlug }}"
                                v-html='item.productName'
                                class="block font-bold text-black text-sm leading-tight"
                            )
                        div(
                            v-if="item.fitmentData"
                            class="pt-1 text-xs leading-none"
                        )
                            | {{ item.fitmentData.year }}
                            | {{ item.fitmentData.makeName }}
                            | {{ item.fitmentData.modelName }}&nbsp;
                            span(v-html="item.fitmentData.bedName")
                            | &nbsp;
                            span(v-html="item.fitmentData.bodyName")
                            | &nbsp;
                            span(v-html="item.fitmentData.engineName")
                            | &nbsp;
                            span(v-html="item.fitmentData.appNote")
                        div(class="flex justify-end mt-4")
                            div(
                                data-cypress='qtySelector'
                                class="flex flex-wrap justify-end w-1/2"
                            )
                                div(
                                    v-if="!item.isVirtualItem"
                                    class="flex flex-col"
                                )
                                    div(class="flex")
                                        label(class="m-1 text-sm") QTY
                                        select(
                                            v-model='item.qty'
                                            @change='updateQty(item.itemId, item.qty)'
                                            class="flex pr-3 h-auto border border-gray-light rounded-sm items-center justify-start"
                                        )
                                            option(v-for='n in maxQty(item.qty)', :value='n') {{ n }}
                                    div(
                                        @click='removeItem(item.itemId)'
                                        class="text-action text-xs font-bold text-right cursor-pointer"
                                    ) Remove
                                div(v-else class="flex")
                                    label(class="m-1 text-sm") QTY
                                    div(class="w-16 text-center py-3 pl-10 pr-0") {{ item.qty }}
                            div(class="text-right w-1/2")
                                template(v-if="item.warranty || item.installation")
                                    div(
                                        data-cypress='saleSubtotal'
                                        class="text-black text-lg font-bold"
                                    ) {{ formatCents(item.itemSubtotal) }}
                                    ExtendPrice(
                                        v-if="$extend.enabled && item.warranty"
                                        :warranty="item.warranty"
                                    )
                                    InstallernetPrice(
                                        v-if="$installernet.enabled && item.installation"
                                        :installation="item.installation"
                                    )
                                template(v-else)
                                    div(
                                        v-if='item.originalPrice != item.salePrice'
                                        data-cypress='originalSubtotal'
                                        class="text-gray line-through text-sm"
                                    ) {{ formatCents(item.originalSubtotal) }}
                                    div(
                                        data-cypress='saleSubtotal'
                                        class="text-black text-lg font-bold"
                                    ) {{ formatCents(item.saleSubtotal) }}

        div(class="flex flex-wrap justify-between items-center relative text-right text-sm text-gray-dark font-bold border-b border-t lg:border-t-0 border-gray-light py-3")
            div(
                v-if="$device.isSmall && $sitewideData.config.shareCartEnabled && cart.itemCount"
                class="mr-auto flex items-center"
            )
                a(
                    href="#"
                    @click="$overlay.show('sc')"
                    class="flex font-bold text-sm mr-1"
                )
                    SaveIcon(class="w-5 h-4 text-action fill-current align-middle mb-1 mr-1")
                    | Email Cart
                Tooltip(
                    align="left"
                    text="We'll email you a link to this cart so you can use it on another browser or device"
                )
                    infoIcon(class="w-3 h-4 text-action fill-current align-middle mb-1 mr-1 focus:outline-none")
            div(class="ml-auto")
                span Subtotal
                span(class="text-xs ml-1") ({{ numberize(cart.itemCount, 'Item') }}):
                span(class="text-black text-lg ml-2") {{ formatCents(cart.total) }}

    div(class="pt-3 text-right")
        div(class="lg:flex lg:justify-between lg:items-center")
            div(
                v-if="!$device.isSmall && $sitewideData.config.shareCartEnabled && cart.itemCount"
                class="relative flex items-center text-action"
            )
                a(
                    href="#"
                    @click="$overlay.show('sc')"
                    class="flex text-action font-bold text-sm"
                )
                    SaveIcon(class="w-5 h-4 fill-current align-middle mb-1 mr-1")
                    | Email Cart
                Tooltip(
                    align="left"
                    text="We'll email you a link to this cart so you can use it on another browser or device"
                )
                    infoIcon(class="w-3 h-4 fill-current align-middle mb-1 ml-1 focus:outline-none")

            div(class="lg:flex lg:ml-auto lg:flex-row")
                BaseButton(
                    v-if="showContinueShopping"
                    color="SECONDARY"
                    @click.native="close"
                    class="w-full lg:w-auto mb-2 lg:mb-0"
                ) Continue Shopping

                BaseButton(
                    v-if="cart.itemCount"
                    icon="PADLOCK"
                    @click.native="$push.to({ name: 'cart'})"
                    class="w-full lg:w-auto lg:ml-2"
                ) Checkout
</template>

<script>
import BaseButton from '@/components/baseButton'
import { formatCents, numberize } from '@/helpers/filters'
import { maxQty } from '@/helpers/mixins'
import Tooltip from '@/components/tooltip'
import SaveIcon from '@/assets/common/save?inline'
import infoIcon from '@/assets/common/info?inline'
const ExtendPrice = () =>
    import(/* webpackChunkName: "ExtendPrice" */ '@/integrations/extend/price')
const InstallernetPrice = () =>
    import(
        /* webpackChunkName: "integrations-installernet-price" */ '@/integrations/installernet/price'
    )

export default {
    name: 'MiniCart',
    components: {
        BaseButton,
        SaveIcon,
        infoIcon,
        Tooltip,
        ExtendPrice,
        InstallernetPrice,
    },
    props: {
        showContinueShopping: {
            type: Boolean,
            default: false,
        },
        showDropShadow: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            isShowingTooltip: false,
        }
    },
    computed: {
        cart() {
            // changing qty actually modifies the model, and we don't want to change it outside of vuex so lets make a copy
            return JSON.parse(JSON.stringify(this.$store.state.cart.data))
        },
        itemCount() {
            return this.cart.itemCount || 0
        },
    },
    methods: {
        formatCents,
        numberize,
        maxQty,
        close() {
            this.$emit('close')
        },
        removeItem(itemId) {
            this.$store.dispatch('cart/removeItem', itemId)
        },
        updateQty(itemId, qty) {
            this.$store.dispatch('cart/updateQty', { itemId, qty })
        },
    },
}
</script>

<style lang="stylus" scoped>
@screen lg
    .items
        max-height 525px
@screen md
    .modal-xicon
        top: 0
        right: -40px
</style>
